import { AppInterface } from '@/enums/app-interface.enum';
import { type AppToWebMessage, sendToApp } from '@/lib/app-interface';
import { logoutApi } from '@/services/auth.service';
import { decodeJwtSubject } from '@/services/token-ttl';
import { useAuthStore } from '@/stores/auth.store';
import { useUserStore } from '@/stores/user.store';
import { create } from 'zustand';

/**
 * 앱 웹뷰 ↔ 네이티브 세션 연결.
 *
 * 앱은 토큰 정본을 네이티브에 두고(`/v1/auth/*` 를 직접 부른다), 웹뷰에는 accessToken 만
 * 넘겨준다(REQ_AUTH_TOKEN → RES_AUTH_TOKEN).
 *
 * **네이티브 세션을 지원하는 앱(`nativeSession: true`)에서는 앱 세션이 유일한 기준이다** —
 * 웹과 앱이 서로 다른 계정이거나, 앱은 로그아웃인데 웹만 로그인인 상태를 만들지 않는다.
 * - 앱이 토큰을 주면 웹은 그 토큰을 쓴다(다른 회원이면 웹 상태를 버리고 앱 회원으로 바꾼다).
 * - 앱이 "토큰 없음"이면 웹도 로그아웃한다(예전 웹 전용 세션은 서버에서도 끊는다).
 * - 웹에서 로그인하면 `app` 채널로 받아 그 세션을 앱에 넘긴다(`handOffSessionToApp`).
 * - 갱신은 앱에 시킨다(웹 쿠키로 스스로 갱신하지 않는다 — setup-auth-interceptors).
 *
 * 지원 표시가 없거나 false 인 앱(예전 버전·Android 5.x 처럼 토큰을 안전하게 저장할 수 없는 기기)은
 * 예전처럼 웹이 자기 세션을 갖는다 — 앱에 토큰이 있을 때만 받아 쓰고 지우지 않는다.
 */

type AppMessageListener = (message: AppToWebMessage) => void;

/**
 * 앱 응답을 기다리는 최대 시간.
 *
 * 브리지에 답한 적이 있는 웹뷰(`APP_BRIDGE_SUPPORT_KEY`)는 수 ms 안에 답하므로 넉넉히 둔다.
 * 답한 적이 없는 웹뷰는 **구버전 앱이거나 브리지가 없는 웹뷰일 가능성이 높아** 짧게 끊는다 — 출시된 앱(브리지 미탑재)에서
 * 5초를 다 기다리느라 웹뷰의 첫 API 요청이 매번 5초 늦었다(2026-10-01 운영, 학원찾기 목록과
 * 하단 슬라이딩 팝업이 5.3초 뒤에 떴다).
 */
const APP_TOKEN_TIMEOUT_MS = 3_000;
const APP_TOKEN_TIMEOUT_UNKNOWN_MS = 800;

/**
 * **이 웹뷰가** 토큰 브리지에 답한 적이 있는가 — 웹뷰마다 따로인 sessionStorage 에 둔다.
 *
 * localStorage(앱 전체 공유)에 두면 안 된다. 앱 안에서도 브리지에 답하는 웹뷰는 학원찾기
 * 하나뿐이고(android `WebViewAcademySearch`·iOS `AcademySearchWebViewController`), 시험안내·
 * 모의고사 결과·메인 팝업 웹뷰(android `WebViewScene`·iOS `WebViewController`)는 답하지 않는다.
 * 공유 기록이면 학원찾기에서 한 번 답한 뒤 그 웹뷰들이 매 문서 시간 초과까지 기다린다.
 * 같은 웹뷰 안의 다음 문서(페이지 이동·새로고침)는 이 기록으로 답을 기다린다.
 */
const APP_BRIDGE_SUPPORT_KEY = 'dp_appAuthBridge';

function readBridgeSupport(): boolean {
  try {
    return window.sessionStorage.getItem(APP_BRIDGE_SUPPORT_KEY) === '1';
  } catch {
    return false;
  }
}

function rememberBridgeSupport(): void {
  try {
    window.sessionStorage.setItem(APP_BRIDGE_SUPPORT_KEY, '1');
  } catch {
    // 저장소를 못 쓰면 매번 "모름"으로 동작할 뿐이다
  }
}

/**
 * 첫 API 요청이 앱의 토큰 응답을 기다려도 되는가 — 브리지에 답한 적이 있는 웹뷰만.
 * 답한 적이 없는 웹뷰(구버전 앱·브리지 없는 웹뷰)에서 기다리면 시간 초과까지 모든 요청이 멈춘다.
 */
export function shouldAwaitAppToken(): boolean {
  return (
    isInAppWebView() &&
    !useAppAuthBridgeStore.getState().resolved &&
    readBridgeSupport()
  );
}

/**
 * 세션을 앱에 넘긴 직후 잠깐은 "토큰 없음" 응답을 무시한다 — 넘기기 전에 보낸 요청의 답이
 * 늦게 와서 방금 한 로그인을 지우지 않게.
 */
const HANDOFF_GRACE_MS = 5_000;

type AppAuthBridgeState = {
  /** 첫 토큰 요청에 앱이 답했거나(또는 시간 초과) 앱 웹뷰가 아니다 */
  resolved: boolean;
  /** 앱이 네이티브 세션을 지원하는가. null = 모름(응답 전·구버전 앱) */
  nativeSession: boolean | null;
};

export const useAppAuthBridgeStore = create<AppAuthBridgeState>(() => ({
  resolved: false,
  nativeSession: null,
}));

type AppTokenResponse = { token: string | null; nativeSession: boolean | null };

let pendingRequest: Promise<string | null> | null = null;
let started = false;
let handedOffAt = 0;

export function isInAppWebView(): boolean {
  if (typeof window === 'undefined') return false;
  return Boolean(
    window.Android?.onAndroidHandler ||
    window.webkit?.messageHandlers?.iosHandler,
  );
}

/** 앱 세션이 기준인 앱 웹뷰인가 — 이때 웹 로그인은 `app` 채널로 받아 앱에 넘긴다 */
export function isNativeSessionApp(): boolean {
  return (
    isInAppWebView() && useAppAuthBridgeStore.getState().nativeSession === true
  );
}

/**
 * 페이지별 핸들러(`registerWebViewAppMessageHandler`, 한 자리만 있다)와 별도로 앱 메시지를
 * 받는다. layout 의 `onAppMessage` 가 둘 다 부른다.
 */
export function addAppMessageListener(
  listener: AppMessageListener,
): () => void {
  const listeners = (window.__appMessageListeners ??= new Set());
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function parseAppTokenResponse(payload: unknown): AppTokenResponse {
  try {
    const parsed = (
      typeof payload === 'string' ? JSON.parse(payload) : payload
    ) as { accessToken?: unknown; nativeSession?: unknown } | undefined;
    const token = parsed?.accessToken;
    const native = parsed?.nativeSession;
    return {
      token: typeof token === 'string' && token.length > 0 ? token : null,
      nativeSession: typeof native === 'boolean' ? native : null,
    };
  } catch {
    return { token: null, nativeSession: null };
  }
}

/** 웹에 남은 로그인 흔적을 지우고 화면을 처음부터 다시 그린다(캐시된 회원 화면 제거) */
function resetWebSession(): void {
  // 예전 웹 전용 세션의 refresh 쿠키가 남아 있으면 서버에서도 끊는다(실패해도 로컬은 지운다)
  void logoutApi().finally(() => {
    useAuthStore.getState().clearTokens();
    useUserStore.getState().clearUser();
    window.location.reload();
  });
}

/** 앱이 알려 준 세션 상태를 웹에 반영한다 */
function applyAppSession({ token, nativeSession }: AppTokenResponse): void {
  if (nativeSession !== null) {
    useAppAuthBridgeStore.setState({ nativeSession });
  }
  const auth = useAuthStore.getState();

  if (nativeSession !== true) {
    // 예전 방식 — 앱에 토큰이 있을 때만 받아 쓰고, 없다고 웹 세션을 지우지 않는다
    if (token) auth.setTokens(token);
    return;
  }

  if (token) {
    const webUser = decodeJwtSubject(auth.accessToken);
    const appUser = decodeJwtSubject(token);
    if (auth.accessToken && webUser !== appUser) {
      // 다른 회원 — 앱 회원으로 바꾸고 웹의 회원 정보·캐시를 버린다
      auth.setTokens(token);
      useUserStore.getState().clearUser();
      window.location.reload();
      return;
    }
    auth.setTokens(token);
    return;
  }

  if (auth.accessToken && Date.now() - handedOffAt > HANDOFF_GRACE_MS) {
    resetWebSession();
  }
}

/**
 * 앱에 accessToken 을 요청한다. `refresh` 는 만료·401 때 — 앱이 refreshToken 으로 갱신해
 * 새 accessToken 을 준다. 앱 웹뷰가 아니거나 응답이 없으면 null.
 */
export function requestAppAccessToken(
  kind: 'token' | 'refresh' = 'token',
): Promise<string | null> {
  if (!isInAppWebView()) return Promise.resolve(null);
  if (pendingRequest) return pendingRequest;

  pendingRequest = new Promise<string | null>((resolve) => {
    let done = false;
    const finish = (response: AppTokenResponse | null) => {
      if (done) return;
      done = true;
      off();
      clearTimeout(timer);
      pendingRequest = null;
      if (response) applyAppSession(response);
      useAppAuthBridgeStore.setState({ resolved: true });
      resolve(response?.token ?? null);
    };
    const off = addAppMessageListener((message) => {
      if (message.type === AppInterface.RES_AUTH_TOKEN) {
        rememberBridgeSupport();
        finish(parseAppTokenResponse(message.payload));
      }
    });
    const timer = setTimeout(
      () => finish(null),
      readBridgeSupport() ? APP_TOKEN_TIMEOUT_MS : APP_TOKEN_TIMEOUT_UNKNOWN_MS,
    );
    sendToApp(
      kind === 'refresh'
        ? AppInterface.REQ_AUTH_REFRESH
        : AppInterface.REQ_AUTH_TOKEN,
    );
  });
  return pendingRequest;
}

/**
 * 웹에서 방금 로그인·가입해 받은 세션을 앱에 넘긴다(`app` 채널로 받아 refreshToken 이 body 에
 * 있을 때). 웹은 accessToken 만 들고, 앱이 정본이 된다.
 */
export function handOffSessionToApp(
  accessToken: string,
  refreshToken: string,
): void {
  handedOffAt = Date.now();
  useAuthStore.getState().setTokens(accessToken);
  sendToApp(AppInterface.REQ_AUTH_SESSION, { accessToken, refreshToken });
}

/**
 * 클라이언트 진입 시 한 번. 앱 웹뷰면 토큰을 받아 오고, 앱이 먼저 보내 주는 토큰(로그인·갱신·
 * 로그아웃 직후)도 반영한다. 화면으로 돌아올 때마다 다시 받는다 — 그 사이 네이티브에서
 * 로그인·로그아웃했을 수 있다.
 */
export function startAppAuthBridge(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  if (!isInAppWebView()) {
    useAppAuthBridgeStore.setState({ resolved: true });
    return;
  }

  addAppMessageListener((message) => {
    // 요청에 대한 답은 requestAppAccessToken 이 반영한다 — 여기선 앱이 먼저 보낸 것만
    if (message.type === AppInterface.RES_AUTH_TOKEN && !pendingRequest) {
      rememberBridgeSupport();
      applyAppSession(parseAppTokenResponse(message.payload));
    }
  });
  void requestAppAccessToken('token');
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void requestAppAccessToken('token');
    }
  });
}
